import React from "react";
import {
  WatchedFunction,
  InputGroup,
  IGroupValidationResult,
  Input,
  IInputConfig,
  required,
  maxLength,
  pattern,
  QuestionTooltipProps,
  QuestionTooltip,
} from "component-library";

import { IAgency } from "models";
import {
  IAddNewAgencyFormContent,
  AgencyForm,
  FORM_TO_AGENCY_MAPPING,
  AgencyFormValidation,
  AGENCY_TO_FORM_MAPPING,
} from "./AddNewAgencyFormContent";

import "./AddNewAgencyForm.scss";

const VALIDATE = new WatchedFunction(() => undefined);

export interface IAddNewAgencyFormProps {
  content: IAddNewAgencyFormContent;
  agency?: IAgency;
  isFormSubmitted: boolean;
  updateAgency: (agency: IAgency) => void;
  updateFormValidity: (isFormValid: boolean) => void;
}

interface IAddNewAgencyState {
  form: AgencyForm;
  validationData: AgencyFormValidation;
}

export class AddNewAgencyForm extends React.Component<
  IAddNewAgencyFormProps,
  IAddNewAgencyState
> {
  constructor(props: Readonly<IAddNewAgencyFormProps>) {
    super(props);

    this.state = this.getInitialFormState();
  }

  public render() {
    const { form } = this.state;
    const formConfig = this.getFormConfiguration(form);

    return (
      <InputGroup
        className="p-add-new-agency-form"
        validationDone={this.validationPerformed}
        triggerValidation={VALIDATE}
        inputConfigs={formConfig}
      >
        <div className="p-add-new-agency-form__controls">
          {this.renderInput("agencyName", true)}
          {this.renderInput("email")}
          {this.renderInput("phone")}
          {this.renderInput("webSite", true)}
          {this.renderInput("address")}
          {this.renderInput("postCode")}
        </div>
      </InputGroup>
    );
  }

  private renderInput = (
    name: keyof AgencyForm,
    hasHelper: boolean = false
  ) => {
    const { content, isFormSubmitted } = this.props;
    const { form, validationData } = this.state;

    const helper = hasHelper ? this.getHelper(content.help[name]) : undefined;

    return (
      <div className="p-add-new-agency-form__control">
        <Input
          id={name}
          name={name}
          type="text"
          label={content.form[name]}
          value={form[name]}
          valueChanged={this.inputChanged(name)}
          invalid={validationData[name].invalid}
          isFormSubmitted={isFormSubmitted}
          annex={helper}
          validationMessage={validationData[name].validationMessage}
        />
      </div>
    );
  };

  private getHelper = (content: QuestionTooltipProps) => (
    <QuestionTooltip {...content} />
  );

  private inputChanged = (name: keyof AgencyForm) => {
    const form = { ...this.state.form };

    return (value: any) => {
      form[name] = value;
      this.setState({ form }, () => {
        VALIDATE.call();
        const agency = this.mapFormToAgency(form);
        this.props.updateAgency(agency);
      });
    };
  };

  private getInitialFormState(): IAddNewAgencyState {
    const newState = { form: {}, validationData: {} } as IAddNewAgencyState;

    Object.keys(AGENCY_TO_FORM_MAPPING).forEach((key) => {
      const formKey = AGENCY_TO_FORM_MAPPING[key];

      newState.form[formKey] = "";
      newState.validationData[formKey] = {
        invalid: false,
        validationMessage: "",
      };
    });

    return newState;
  }

  private getFormConfiguration = (form: AgencyForm) => {
    const config: IInputConfig[] = [];
    const validationFunctions = this.getValidationFunctions();

    Object.keys(FORM_TO_AGENCY_MAPPING).forEach((key) => {
      config.push({
        name: key,
        value: form[key],
        validators: validationFunctions[key],
      });
    });

    return config;
  };

  private getValidationFunctions = () => {
    const { agencyName, email, phone, webSite, address, postCode } =
      this.props.content.validation;

    const validators = {
      agencyName: [
        required(agencyName.required),
        maxLength(50, agencyName.maxLength),
      ],
      email: [
        required(email.required),
        pattern(new RegExp("@"), email.email),
        maxLength(50, email.maxLength),
      ],
      phone: [required(phone.required), maxLength(30, phone.maxLength)],
      webSite: [maxLength(50, webSite.maxLength)],
      address: [maxLength(50, address.maxLength)],
      postCode: [maxLength(8, postCode.maxLength)],
    };

    return validators;
  };

  private mapFormToAgency = (form: AgencyForm): IAgency => {
    const { agency } = this.props;

    const updatedAgency = Object.assign({}, agency);
    Object.keys(FORM_TO_AGENCY_MAPPING).forEach(
      (key) => (updatedAgency[FORM_TO_AGENCY_MAPPING[key]] = form[key])
    );

    return updatedAgency;
  };

  private validationPerformed = (result: IGroupValidationResult) => {
    const validationData = {
      ...this.state.validationData,
      ...result.validationData,
    };
    this.setState({ validationData });
    this.props.updateFormValidity(!result.isGroupInvalid);
  };
}
